<template>
  <div v-if="applicationDetails" :key="applicationDetails.id" class="w-full">
    <ShowApplicationSkeleton v-if="isLoading" />
    <div v-else class="w-full">
      <div
        class="flex flex-row pr-28 md:sticky z-[9] bg-transparent border-b-1 border-grey-divider-subdued border-solid w-full md:bg-white md-1:top-80"
      >
        <div class="flex flex-col w-full p-16">
          <div class="flex flex-row items-center">
            <button class="inline-block md:hidden" @click="showPhoto = true">
              <Avatar class="align-middle mb-8" :imageUrl="sitter.photoUrl" size="large" :name="sitter.displayName + ' avatar'" />
            </button>
            <h1 class="desktop-ellipsis align-middle text-h2 md:mt-8 md:mb-24 pl-8 md:pl-0 md:ml-0 w-full">
              {{ sitter.displayName }}
            </h1>
          </div>
          <p class="text-body desktop-ellipsis">
            Applied to your job
            <a href="/seeker/my-jobs/opened" class="text-grey-clickable underline">{{ applicationDetails?.job?.title }}</a>
          </p>
        </div>
        <div class="flex items-center absolute top-0 right-0 md:static">
          <button class="hidden md:block" @click="showPhoto = true">
            <Avatar :imageUrl="sitter.photoUrl" size="mega" :name="sitter.displayName + ' avatar'" class="mr-28" />
          </button>
          <Popup
            v-model="showPhoto"
            :popupHeader="sitter.displayName + (sitter.photoUrl ? `'s profile photo` : ` doesn't have a profile photo`)"
          >
            <img v-if="sitter.photoUrl" :src="sitter.photoUrl" class="w-full" />
            <Button v-else variant="primary" class="w-full mt-1" value="OK" @click="showPhoto = false" />
          </Popup>
          <Button id="modal-toggle" variant="outlined" class="border-0 p-24" @click="showPopup = true">
            <MoreIcon color="#4B4C52" />
          </Button>
          <Popup v-model="showPopup" popupHeader="Manage application">
            <Button
              v-if="sitter?.state == 'active'"
              variant="outlined"
              value="view full profile"
              :to="sitterProfileURL"
              class="w-full mb-16"
            />
            <Button variant="outlined" :value="'Report ' + sitter.displayName" :href="reportURL" class="w-full" />
          </Popup>
        </div>
      </div>
      <div class="bg-grey-background-subdued">
        <div v-if="sitter?.state !== 'active'" class="flex flex-col">
          <AlertBanner :status="3" class="w-full">
            {{ alertBannerText }}
          </AlertBanner>
          <ReplyContainer v-if="!isNotAFit" class="lg:w-1/3 self-end mb-80 p-16" />
        </div>

        <div v-else class="flex flex-col lg:flex-row pt-16 pb-80 md:pb-8">
          <div class="w-full lg:w-2/3 px-16 pb-5">
            <div v-if="sitter?.state === 'active'">
              <div>
                <Card v-show="!isQuickApply" class="w-full mb-24">
                  <h3 class="font-medium text-m1 mb-8">Cover letter</h3>
                  <ViewMoreTextBox viewMoreButtonText="View full cover letter">{{
                    applicationDetails.reviewedApplicationText
                  }}</ViewMoreTextBox>
                </Card>
                <Card v-if="applicationDetails.matchingRequirements.length || isBasic" class="w-full mb-24">
                  <ApplicantMatch :applicationDetails="applicationDetails" :isBasic="isBasic" />
                </Card>
                <Card class="w-full mb-24">
                  <h3 class="font-medium text-m1">About {{ sitter.displayName }}</h3>
                  <IconList :items="applicantDetails" class="mt-8" @item-click="(item) => item.clickHandler()" />
                </Card>
                <Card class="w-full mb-24">
                  <h3 class="font-medium text-m1 mb-14">Background checks & certifications</h3>
                  <SitterBadgeList class="mb-16" :badges="sitter.badges" :idv="sitter.idv" />
                  <a href="/background-checks/whats-covered#background-check-badges" class="text-grey-clickable underline text-s">
                    View what these badges mean
                  </a>
                </Card>
                <Card v-show="!isQuickApply && hasBio" class="w-full mb-24">
                  <h3 class="font-medium text-m1 mb-8">Bio</h3>
                  <ViewMoreTextBox viewMoreButtonText="View full bio">{{
                    sitter.careTypeDetails[$store.getters['applications/applicantCareTypeKey']].bio
                  }}</ViewMoreTextBox>
                </Card>
                <Card v-if="sitter.reviews.edges.length" class="w-full mb-24">
                  <RatingsAndReviews v-model="reviewsDisplayedCount" />
                </Card>
                <Card v-if="sitterHasEducation" class="w-full mb-24">
                  <Education :selectedSitter="sitter" />
                </Card>
                <Card v-if="adventures.length > 0" ref="adventuresSection" class="w-full mb-20">
                  <AdventuresList
                    :adventures="adventures"
                    :sitter="sitter"
                    :variant="'application'"
                    :display-name="sitter.displayName"
                  />
                </Card>
              </div>
              <div v-if="applicantIsDismissed" class="md:hidden -mt-20">
                <div
                  class="w-full bottom-0 left-0 border-t-1 border-solid border-grey-divider-subdued bg-foreground mt-16 mb-16"
                />
                <HiringTip />
              </div>
            </div>
          </div>
          <ReplyContainer class="w-full lg:w-1/3 mb-[140px] pr-16" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
// @ts-nocheck

// Components
import AlertBanner from '../../components/alert_banner/AlertBanner.vue';
import Avatar from '../../components/avatar/Avatar.vue';
import Button from '../../components/button/Button.vue';
import Card from '../../components/card/Card.vue';
import Popup from '../../components/popup/Popup.vue';
import ReplyContainer from './ReplyContainer/ReplyContainer.vue';
import RatingsAndReviews from './RatingsAndReviews.vue';
import ShowApplicationSkeleton from './ShowApplicationSkeleton.vue';
import ApplicantMatch from './ApplicantMatch.vue';
import Education from './Education.vue';
import IconList from '@/components/icon_list/IconList.vue';
import SitterBadgeList from '@/components/sitter_badge_list/SitterBadgeList.vue';

// Icons
import HeartOutlineIcon from '@/graphics/icons/HeartOutlineIcon.vue';
import StarOutlineIcon from '@/graphics/icons/StarOutlineIcon.vue';
import PlaceOutlineIcon from '@/graphics/icons/PlaceOutlineIcon.vue';
import DollarIcon from '@/graphics/icons/DollarIcon.vue';
import AdventuresOutlineIcon from '@/graphics/icons/AdventuresOutlineIcon.vue';
import MoreIcon from '@/graphics/icons/MoreIcon.vue';
import ViewMoreTextBox from '@/components/view_more_text_box/ViewMoreTextBox.vue';
import HiringTip from './ReplyContainer/HiringTip.vue';
import AdventuresList from '@/components/adventures_list/AdventuresList.vue';

// Lib
import { FETCH_APPLICATION_DETAILS } from '@/graphql/applications/queries';
import { Account } from '@/lib/public_api/Account';
import getProviderProfileUrl from '@/helpers/providerProfileUrl';

const defaultReviewsDisplayedCount: number = 3;

export default {
  name: 'ShowApplication',
  components: {
    AlertBanner,
    Avatar,
    Button,
    Card,
    Popup,
    ReplyContainer,
    RatingsAndReviews,
    ShowApplicationSkeleton,
    ApplicantMatch,
    Education,
    IconList,
    MoreIcon,
    ViewMoreTextBox,
    HiringTip,
    AdventuresList,
    SitterBadgeList,
  },
  data() {
    return {
      showPopup: false,
      showPhoto: false,
      reviewsDisplayedCount: defaultReviewsDisplayedCount,
    };
  },
  computed: {
    applicantIsDismissed(): boolean {
      const { parentInterestStatus } = this.$store.getters['applications/applicationDetails'];
      return parentInterestStatus !== 'NOT_A_FIT' && parentInterestStatus !== 'INTERESTED';
    },
    applicationDetails(): any {
      return this.$store.getters['applications/applicationDetails'] || {};
    },
    sitter(): any {
      return this.applicationDetails?.sitter || {};
    },
    adventures(): any {
      const adventuresEdges = this.sitter?.adventures?.edges || [];
      const allAdventures = adventuresEdges.map(({ node }) => node.adventure);
      return allAdventures.filter((adventure) => adventure.status === 'active');
    },
    isNotAFit(): boolean {
      return this.$store.getters['applications/applicationDetails'].parentInterestStatus === 'NOT_A_FIT';
    },
    isLoading(): boolean {
      return this.$store.getters['applications/showApplicationLoading'];
    },
    account(): Account {
      return this.$store.getters['account/getAccount'];
    },
    applicantDetails() {
      return [
        {
          icon: HeartOutlineIcon,
          text: this.showExperience ? `${this.pluralizedExperienceText} paid experience` : '',
        },
        {
          icon: StarOutlineIcon,
          text: this.hasReviews ? `${this.sitter.reviews?.starAggregate} star rating` : '',
        },
        {
          icon: PlaceOutlineIcon,
          text: this.locationText,
        },
        {
          icon: DollarIcon,
          text: this.payRateDisplay,
        },
        {
          icon: AdventuresOutlineIcon,
          text:
            this.adventures.length > 0
              ? `Offers ${this.adventures.length === 1 ? `${this.adventures[0].adventureCategoryName} Adventure` : `${this.adventures.length} Adventures`}`
              : '',
        },
      ];
    },
    payRateDisplay() {
      const careTypeKey = this.$store.getters['applications/applicantCareTypeKey'];
      const details = this.sitter.careTypeDetails[careTypeKey];

      if (!details || details.maxPayRate === null) {
        return '';
      }

      if (careTypeKey === 'petCare') {
        const maxRate = details.maxPayRate / 100;
        return `$${maxRate}/visit`;
      }

      if (careTypeKey === 'companionCare') {
        const maxRate = details.maxPayRate / 100;
        return `$${maxRate}/hour`;
      }

      if (careTypeKey === 'babysitter' || careTypeKey === 'housekeeping' || careTypeKey === 'nanny') {
        const minRate = details.minPayRate !== null ? details.minPayRate / 100 : 0;
        const maxRate = details.maxPayRate / 100;
        return `$${minRate}–${maxRate}/hr`;
      }

      return '';
    },

    isChildCare(): boolean {
      return this.$store.getters['applications/applicantCareTypeKey'] === 'childCare';
    },
    sitterHasEducation(): boolean {
      const { education } = this.sitter;

      const isInvalidSchoolName = education.schoolName === 'Select' || education.schoolName === '';
      const isUndesirableLevel = [
        '',
        'some_high_school',
        'high_school_degree',
        'some_college',
        'select',
        'some_graduate_school',
        null,
      ].includes(education.levelOfEducation);

      return !isInvalidSchoolName && !isUndesirableLevel;
    },
    showExperience(): boolean {
      const careTypeKey = this.$store.getters['applications/applicantCareTypeKey'];
      const experience = this.sitter.careTypeDetails[careTypeKey].yearsExperience;
      return !!experience;
    },
    isBasic(): boolean {
      return this.$store.getters['account/getAccount']?.isBasic() || false;
    },
    hasBio(): boolean {
      const careTypeKey = this.$store.getters['applications/applicantCareTypeKey'];
      return !!this.sitter?.careTypeDetails[careTypeKey].bio;
    },
    reportURL(): string {
      if (!this.applicationDetails) return '';

      const isStaging: boolean = window.location.href.includes('staging.sittercity.com');
      const domainURL: string = `https://admin${isStaging ? '-staging' : ''}.sittercity.com`;

      const adminLink: string = `${domainURL}/admins/accounts/${this.sitter.id}/messages/${this.applicationDetails.memberCommunicationId}`;
      return `https://support.sittercity.com/hc/en-us/requests/new?ticket_form_id=360000078194&related_url=${adminLink}&sitter_uuid=${this.sitter.id}`;
    },
    isQuickApply(): boolean {
      return this.applicationDetails?.reviewedApplicationText?.includes('This is an automated message from Sittercity.') || false;
    },
    hasReviews(): boolean {
      return this.sitter?.reviews?.totalReviewCount > 0;
    },
    locationText(): string {
      const distance = this.applicationDetails?.distance;

      if (distance !== undefined && distance < 1.1) {
        return `< 1 mile • ${this.sitter?.location?.cityname}, ${this.sitter?.location?.stateabbr}`;
      }

      return `${distance?.toFixed(1)} miles • ${this.sitter?.location?.cityname}, ${this.sitter?.location?.stateabbr}`;
    },
    sitterProfileURL(): string {
      const careTypeKey = this.$store.getters['applications/applicantCareTypeKey'];
      return getProviderProfileUrl(this.sitter?.id, careTypeKey, this.sitter?.providerType) || '';
    },
    alertBannerText(): String {
      if (this.sitter?.state === 'closed') {
        return "This user can't be contacted because their Sittercity account is inactive.";
      }
      if (this.sitter?.state === 'terminated' || this.sitter?.state === 'scammer') {
        return 'This user has been removed from the Sittercity network. Check your email for additional information.';
      }
      return 'This user is no longer a member in the Sittercity network.';
    },
    pluralizedExperienceText(): String {
      const careTypeKey = this.$store.getters['applications/applicantCareTypeKey'];
      if (this.sitter.careTypeDetails[careTypeKey].yearsExperience === 1) {
        return `${this.sitter.careTypeDetails[careTypeKey].yearsExperience} year`;
      }
      return `${this.sitter.careTypeDetails[careTypeKey].yearsExperience} years`;
    },
  },
  watch: {
    $route(): void {
      // this watcher can be used to reset data values when switching applicants
      this.reviewsDisplayedCount = defaultReviewsDisplayedCount;
      this.$store.dispatch('applications/showApplicationLoading', true);
      this.fetchApplicationDetails();
    },
    reviewsDisplayedCount(): void {
      this.fetchApplicationDetails();
    },
  },
  created() {
    this.fetchApplicationDetails();
  },
  methods: {
    async fetchApplicationDetails() {
      await this.$apollo
        .query({
          query: FETCH_APPLICATION_DETAILS,
          fetchPolicy: 'network-only',
          variables: {
            application_uuid: this.$route?.params?.application_uuid,
            reviewsFetchCount: this.reviewsDisplayedCount,
          },
        })
        .then(({ data }) => {
          this.$store.commit('applications/setApplicationDetails', data.applicationDetails);
        })
        .catch((error: any) => {
          console.error('GraphQL query error:', error);
        });
      this.$store.dispatch('applications/showApplicationLoading', false);
    },
  },
};
</script>

<style scoped>
@media (min-width: 768px) {
  .desktop-ellipsis {
    overflow: hidden;
    -webkit-box-orient: vertical;
    display: -webkit-box;
    -webkit-line-clamp: 1;
  }
}
</style>
